import { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";
import {
  getFeatureFlags,
  saveFeatureFlags,
  getPageStatuses,
  savePageStatuses,
  getStoredLeads,
  updateLeadStatus,
  getAnnouncement,
  saveAnnouncement,
  getSiteContent,
  saveSiteContent,
  getTestimonials,
  updateTestimonialStatus,
  deleteTestimonial,
  getEvents,
  saveEvents,
  getKpis,
  saveKpis,
  getCaseStudies,
  saveCaseStudies,
  getBlogPosts,
  saveBlogPost,
  deleteBlogPost,
  getNewsItems,
  saveNewsItem,
  deleteNewsItem,
  type FeatureFlags,
  type PageStatus,
  type StoredLead,
  type SiteContent,
  type TestimonialItem,
  type EventItem,
  type KpiItem,
  type CaseStudyItem,
  type BlogPost,
  type NewsItem,
} from "../utils/adminStore";

export function AdminPage() {
  const [authenticated, setAuthenticated] = useState<boolean>(() => {
    return sessionStorage.getItem("radiece_admin_auth") === "true";
  });
  const [passcode, setPasscode] = useState("");
  const [authError, setAuthError] = useState("");

  const [activeTab, setActiveTab] = useState<"flags" | "pages" | "leads" | "testimonials" | "blogs" | "news" | "events" | "kpis" | "content">("blogs");
  const [flags, setFlags] = useState<FeatureFlags>(getFeatureFlags());
  const [pages, setPages] = useState<PageStatus[]>(getPageStatuses());
  const [leads, setLeads] = useState<StoredLead[]>(getStoredLeads());
  const [testimonials, setTestimonialsState] = useState<TestimonialItem[]>(getTestimonials());
  const [events, setEventsState] = useState<EventItem[]>(getEvents());
  const [kpis, setKpisState] = useState<KpiItem[]>(getKpis());
  const [caseStudies, setCaseStudiesState] = useState<CaseStudyItem[]>(getCaseStudies());
  const [blogs, setBlogsState] = useState<BlogPost[]>(getBlogPosts());
  const [news, setNewsState] = useState<NewsItem[]>(getNewsItems());
  const [announcement, setAnnouncementState] = useState<string>(getAnnouncement());
  const [siteContent, setSiteContentState] = useState<SiteContent>(getSiteContent());
  const [saveToast, setSaveToast] = useState(false);

  // New Blog Post State
  const [newBlogTitle, setNewBlogTitle] = useState("");
  const [newBlogSlug, setNewBlogSlug] = useState("");
  const [newBlogCategory, setNewBlogCategory] = useState<BlogPost["category"]>("Software Engineering");
  const [newBlogExcerpt, setNewBlogExcerpt] = useState("");
  const [newBlogContent, setNewBlogContent] = useState("");
  const [newBlogTags, setNewBlogTags] = useState("RaBuOS, Architecture, TypeScript");

  // New News Item State
  const [newNewsTitle, setNewNewsTitle] = useState("");
  const [newNewsSlug, setNewNewsSlug] = useState("");
  const [newNewsCategory, setNewNewsCategory] = useState<NewsItem["category"]>("Platform Update");
  const [newNewsSummary, setNewNewsSummary] = useState("");
  const [newNewsContent, setNewNewsContent] = useState("");
  const [newNewsAuthor, setNewNewsAuthor] = useState("Corporate Legal & Compliance Desk");

  // New Event Form Modal
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventCategory, setNewEventCategory] = useState("Software Engineering");
  const [newEventDate, setNewEventDate] = useState("Sep 15, 2026");
  const [newEventTime, setNewEventTime] = useState("10:00 AM - 04:00 PM IST");
  const [newEventVenue, setNewEventVenue] = useState("Kakkanad Innovation Hub");
  const [newEventSeats, setNewEventSeats] = useState("25 Seats Available");

  useEffect(() => {
    const sync = () => {
      setFlags(getFeatureFlags());
      setPages(getPageStatuses());
      setLeads(getStoredLeads());
      setTestimonialsState(getTestimonials());
      setEventsState(getEvents());
      setKpisState(getKpis());
      setCaseStudiesState(getCaseStudies());
      setBlogsState(getBlogPosts());
      setNewsState(getNewsItems());
      setAnnouncementState(getAnnouncement());
      setSiteContentState(getSiteContent());
    };
    window.addEventListener("radiece_admin_update", sync);
    return () => window.removeEventListener("radiece_admin_update", sync);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError("");
    try {
      const res = await apiFetch("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({ passcode }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        sessionStorage.setItem("radiece_admin_auth", "true");
        setAuthenticated(true);
        setAuthError("");
      } else {
        setAuthError(data.error || "Invalid passcode. Access denied.");
      }
    } catch {
      setAuthError("Failed to connect to authentication server.");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("radiece_admin_auth");
    setAuthenticated(false);
  };

  const toggleFlag = (key: keyof FeatureFlags) => {
    const updated = { ...flags, [key]: !flags[key] };
    setFlags(updated);
    saveFeatureFlags(updated);
    triggerToast();
  };

  const togglePageStatus = (path: string) => {
    const now = new Date().toISOString().slice(0, 10);
    const updated = pages.map((p) =>
      p.path === path
        ? {
            ...p,
            status: p.status === "published" ? ("draft" as const) : ("published" as const),
            lastUpdated: now,
          }
        : p
    );
    setPages(updated);
    savePageStatuses(updated);
    triggerToast();
  };

  const handleAddBlog = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBlogTitle || !newBlogContent) return;
    const slug = newBlogSlug || newBlogTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    const post: BlogPost = {
      slug,
      title: newBlogTitle,
      excerpt: newBlogExcerpt || newBlogTitle,
      category: newBlogCategory,
      author: {
        name: "RADIECE Systems Architecture Practice",
        role: "Systems Architecture Lead",
      },
      publishedAt: new Date().toISOString().split("T")[0]!,
      readingTime: "5 min read",
      tags: newBlogTags.split(",").map((t) => t.trim()).filter(Boolean),
      content: newBlogContent,
    };
    saveBlogPost(post);
    setNewBlogTitle("");
    setNewBlogSlug("");
    setNewBlogExcerpt("");
    setNewBlogContent("");
    triggerToast();
  };

  const handleDeleteBlog = (slug: string) => {
    deleteBlogPost(slug);
    triggerToast();
  };

  const handleAddNews = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNewsTitle || !newNewsContent) return;
    const slug = newNewsSlug || newNewsTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    const item: NewsItem = {
      id: `NEWS-${Date.now()}`,
      slug,
      title: newNewsTitle,
      category: newNewsCategory,
      summary: newNewsSummary || newNewsTitle,
      publishedAt: new Date().toISOString().split("T")[0]!,
      author: newNewsAuthor,
      content: newNewsContent,
    };
    saveNewsItem(item);
    setNewNewsTitle("");
    setNewNewsSlug("");
    setNewNewsSummary("");
    setNewNewsContent("");
    triggerToast();
  };

  const handleDeleteNews = (id: string) => {
    deleteNewsItem(id);
    triggerToast();
  };

  const handleApproveTestimonial = (id: string) => {
    updateTestimonialStatus(id, "approved");
    triggerToast();
  };

  const handleRejectTestimonial = (id: string) => {
    updateTestimonialStatus(id, "rejected");
    triggerToast();
  };

  const handleDeleteTestimonialItem = (id: string) => {
    deleteTestimonial(id);
    triggerToast();
  };

  const handleAddEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEventTitle) return;
    const newItem: EventItem = {
      id: `EVT-${Math.floor(100 + Math.random() * 900)}`,
      title: newEventTitle,
      category: newEventCategory,
      date: newEventDate,
      time: newEventTime,
      venue: newEventVenue,
      seats: newEventSeats,
      status: "Registration Open",
    };
    const updated = [...events, newItem];
    setEventsState(updated);
    saveEvents(updated);
    setNewEventTitle("");
    triggerToast();
  };

  const handleDeleteEvent = (id: string) => {
    const updated = events.filter((e) => e.id !== id);
    setEventsState(updated);
    saveEvents(updated);
    triggerToast();
  };

  const handleKpiChange = (id: string, field: keyof KpiItem, val: string) => {
    const updated = kpis.map((k) => (k.id === id ? { ...k, [field]: val } : k));
    setKpisState(updated);
    saveKpis(updated);
    triggerToast();
  };

  const handleSaveContent = (e: React.FormEvent) => {
    e.preventDefault();
    saveAnnouncement(announcement);
    saveSiteContent(siteContent);
    triggerToast();
  };

  const triggerToast = () => {
    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 2000);
  };

  if (!authenticated) {
    return (
      <main className="no-print min-h-screen bg-brand-dark flex items-center justify-center px-4 py-20">
        <div className="bg-[#101c2a] border border-white/20 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-primary/20 border border-primary/40 text-primary flex items-center justify-center mx-auto text-2xl font-extrabold shadow-lg">
            R
          </div>
          <div>
            <h2 className="text-2xl font-extrabold text-white tracking-wider">
              RADIECE CMS Admin Portal
            </h2>
            <p className="text-xs text-slate-300 mt-1">
              Internal Content, Feature Flags & Lead Management Desk
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4 text-left">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Admin Passcode *
              </label>
              <input
                type="password"
                required
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Enter admin passcode"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-primary text-sm font-mono"
              />
            </div>
            {authError && <p className="text-red-400 text-xs font-medium">{authError}</p>}
            <button
              type="submit"
              className="w-full bg-primary hover:bg-sky-400 text-slate-950 font-bold py-3 rounded-xl transition-all text-xs uppercase tracking-wider shadow-lg shadow-primary/20 cursor-pointer"
            >
              Authenticate & Unlock Console →
            </button>
          </form>

          <p className="text-[11px] text-slate-400">
            For authorized corporate administrators only. Internal CIN: U58200KL2026PTC104862
          </p>
        </div>
      </main>
    );
  }

  const pendingTestimonialsCount = testimonials.filter((t) => t.status === "pending").length;

  return (
    <main className="no-print min-h-screen bg-slate-900 text-white pt-24 pb-20">
      {/* Toast Alert */}
      {saveToast && (
        <div className="fixed top-20 right-6 z-50 bg-emerald-600 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-2xl animate-in flex items-center gap-2">
          <span>✓</span> CMS State Updated Live Across Website!
        </div>
      )}

      {/* Header Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-primary/20 border border-primary/40 text-primary flex items-center justify-center font-extrabold text-xl shrink-0">
              R
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-wide">
                RADIECE Enterprise CMS Console
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">
                Manage Blogs, News, Testimonials, KPIs, Leads & Global Feature Flags
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="/"
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold transition-all border border-white/20"
            >
              Public Site →
            </a>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30 rounded-xl text-xs font-bold transition-all cursor-pointer"
            >
              Lock Console 🔒
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mt-6 pb-2 border-b border-white/10">
          {[
            { key: "blogs", label: `Technical Blog (${blogs.length})`, icon: "✍️" },
            { key: "news", label: `News & Dispatches (${news.length})`, icon: "📰" },
            { key: "testimonials", label: `Testimonials (${pendingTestimonialsCount} Pending)`, icon: "⭐" },
            { key: "kpis", label: "KPIs & Impact Stats", icon: "📊" },
            { key: "flags", label: "Feature Flags", icon: "🚩" },
            { key: "pages", label: "Pages & Drafts", icon: "📄" },
            { key: "leads", label: `Leads Inbox (${leads.length})`, icon: "📥" },
            { key: "events", label: "Events & Symposia", icon: "📅" },
            { key: "content", label: "Global Content", icon: "🌐" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeTab === tab.key
                  ? "bg-primary text-slate-950 shadow-md shadow-primary/20"
                  : "bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white border border-white/10"
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* TAB 1: BLOGS CMS */}
        {activeTab === "blogs" && (
          <div className="mt-6 space-y-8">
            {/* Create New Blog Form */}
            <div className="bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-4">
              <h2 className="text-lg font-bold text-white">Publish New Technical Publication</h2>
              <form onSubmit={handleAddBlog} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Article Title *</label>
                    <input
                      type="text"
                      required
                      value={newBlogTitle}
                      onChange={(e) => setNewBlogTitle(e.target.value)}
                      placeholder="e.g. Modern Embedded Systems Architecture"
                      className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Category</label>
                    <select
                      value={newBlogCategory}
                      onChange={(e) => setNewBlogCategory(e.target.value as any)}
                      className="w-full px-3.5 py-2 bg-[#090e17] border border-white/20 rounded-xl text-xs text-white focus:outline-none focus:border-primary"
                    >
                      <option value="Software Engineering">Software Engineering</option>
                      <option value="Embedded & IoT">Embedded & IoT</option>
                      <option value="Cyber Security">Cyber Security</option>
                      <option value="Cloud & SaaS">Cloud & SaaS</option>
                      <option value="Robotics & AI">Robotics & AI</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Custom Slug (Optional)</label>
                    <input
                      type="text"
                      value={newBlogSlug}
                      onChange={(e) => setNewBlogSlug(e.target.value)}
                      placeholder="auto-generated-from-title"
                      className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Tags (Comma Separated)</label>
                    <input
                      type="text"
                      value={newBlogTags}
                      onChange={(e) => setNewBlogTags(e.target.value)}
                      placeholder="ESP32, LoRaWAN, C++"
                      className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Executive Summary / Excerpt *</label>
                  <textarea
                    rows={2}
                    required
                    value={newBlogExcerpt}
                    onChange={(e) => setNewBlogExcerpt(e.target.value)}
                    placeholder="Brief 1-2 sentence overview for SEO meta descriptions and search cards..."
                    className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Markdown Body Content *</label>
                  <textarea
                    rows={8}
                    required
                    value={newBlogContent}
                    onChange={(e) => setNewBlogContent(e.target.value)}
                    placeholder="## Introduction&#10;&#10;Write markdown content here including code blocks, headers, and lists..."
                    className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary font-mono"
                  />
                </div>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary hover:bg-sky-400 text-slate-950 font-bold rounded-xl text-xs uppercase tracking-wider shadow-md cursor-pointer"
                >
                  Publish Article Live →
                </button>
              </form>
            </div>

            {/* Existing Publications List */}
            <div className="bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-4">
              <h2 className="text-lg font-bold text-white">Published Technical Publications</h2>
              <div className="space-y-3">
                {blogs.map((post) => (
                  <div
                    key={post.slug}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div>
                      <span className="text-[10px] font-bold text-primary uppercase px-2 py-0.5 rounded bg-primary/10 mr-2">
                        {post.category}
                      </span>
                      <span className="text-xs font-mono text-slate-400">📅 {post.publishedAt}</span>
                      <h4 className="text-sm font-bold text-white mt-1">{post.title}</h4>
                      <p className="text-xs text-slate-400 font-mono mt-0.5">/#/blog/{post.slug}</p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href={`#/blog/${post.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition-colors"
                      >
                        View ↗
                      </a>
                      <button
                        onClick={() => handleDeleteBlog(post.slug)}
                        className="px-3 py-1.5 bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: NEWS CMS */}
        {activeTab === "news" && (
          <div className="mt-6 space-y-8">
            {/* Create New News Item Form */}
            <div className="bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-4">
              <h2 className="text-lg font-bold text-white">Broadcast New Company Dispatch / Release</h2>
              <form onSubmit={handleAddNews} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Announcement Title *</label>
                    <input
                      type="text"
                      required
                      value={newNewsTitle}
                      onChange={(e) => setNewNewsTitle(e.target.value)}
                      placeholder="e.g. Production Release of RaBuOS v1.2"
                      className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Category</label>
                    <select
                      value={newNewsCategory}
                      onChange={(e) => setNewNewsCategory(e.target.value as any)}
                      className="w-full px-3.5 py-2 bg-[#090e17] border border-white/20 rounded-xl text-xs text-white focus:outline-none focus:border-primary"
                    >
                      <option value="Platform Update">Platform Update</option>
                      <option value="Engineering Dispatch">Engineering Dispatch</option>
                      <option value="Company Release">Company Release</option>
                      <option value="Statutory Notice">Statutory Notice</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Author / Desk</label>
                    <input
                      type="text"
                      value={newNewsAuthor}
                      onChange={(e) => setNewNewsAuthor(e.target.value)}
                      className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Custom Slug (Optional)</label>
                    <input
                      type="text"
                      value={newNewsSlug}
                      onChange={(e) => setNewNewsSlug(e.target.value)}
                      placeholder="auto-generated"
                      className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Summary Overview *</label>
                  <textarea
                    rows={2}
                    required
                    value={newNewsSummary}
                    onChange={(e) => setNewNewsSummary(e.target.value)}
                    placeholder="Short summary displayed in the newsfeed timeline..."
                    className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Full Announcement Body *</label>
                  <textarea
                    rows={6}
                    required
                    value={newNewsContent}
                    onChange={(e) => setNewNewsContent(e.target.value)}
                    placeholder="Full press release content..."
                    className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary font-mono"
                  />
                </div>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary hover:bg-sky-400 text-slate-950 font-bold rounded-xl text-xs uppercase tracking-wider shadow-md cursor-pointer"
                >
                  Broadcast News Dispatch Live →
                </button>
              </form>
            </div>

            {/* Existing News List */}
            <div className="bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-4">
              <h2 className="text-lg font-bold text-white">Active Newsfeed Items</h2>
              <div className="space-y-3">
                {news.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div>
                      <span className="text-[10px] font-bold text-primary uppercase px-2 py-0.5 rounded bg-primary/10 mr-2">
                        {item.category}
                      </span>
                      <span className="text-xs font-mono text-slate-400">📅 {item.publishedAt}</span>
                      <h4 className="text-sm font-bold text-white mt-1">{item.title}</h4>
                      <p className="text-xs text-slate-400 font-mono mt-0.5">/#/news/{item.slug}</p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href={`#/news/${item.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition-colors"
                      >
                        View ↗
                      </a>
                      <button
                        onClick={() => handleDeleteNews(item.id)}
                        className="px-3 py-1.5 bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: TESTIMONIALS CMS */}
        {activeTab === "testimonials" && (
          <div className="mt-6 bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-white">Client & Partner Testimonials Moderation</h2>
              <p className="text-xs text-slate-300 mt-1">
                Approve or reject reviews submitted via the public homepage review modal.
              </p>
            </div>

            <div className="space-y-4">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-white">{t.name}</span>
                      <span className="text-xs text-slate-400">({t.role}, {t.organization})</span>
                      <span className="text-xs text-amber-400">{"⭐".repeat(t.rating)}</span>
                      <span
                        className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                          t.status === "approved"
                            ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                            : t.status === "pending"
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                            : "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                        }`}
                      >
                        {t.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-300 italic">"{t.content}"</p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {t.status !== "approved" && (
                      <button
                        onClick={() => handleApproveTestimonial(t.id)}
                        className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 rounded-lg text-xs font-bold cursor-pointer"
                      >
                        Approve
                      </button>
                    )}
                    {t.status !== "rejected" && (
                      <button
                        onClick={() => handleRejectTestimonial(t.id)}
                        className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded-lg text-xs font-bold cursor-pointer"
                      >
                        Reject
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteTestimonialItem(t.id)}
                      className="px-3 py-1.5 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 rounded-lg text-xs font-bold cursor-pointer"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 4: KPIS & STATS CMS */}
        {activeTab === "kpis" && (
          <div className="mt-6 bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-white">Live Metric Counters & Impact Stats</h2>
              <p className="text-xs text-slate-300 mt-1">
                Customize operational statistics rendered dynamically on the public homepage.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {kpis.map((k) => (
                <div key={k.id} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                  <span className="text-xs font-mono text-primary font-bold">{k.id}</span>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Metric Value</label>
                      <input
                        type="text"
                        value={k.metric}
                        onChange={(e) => handleKpiChange(k.id, "metric", e.target.value)}
                        className="w-full px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-xs font-bold text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Label</label>
                      <input
                        type="text"
                        value={k.label}
                        onChange={(e) => handleKpiChange(k.id, "label", e.target.value)}
                        className="w-full px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-xs text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Subtext</label>
                    <input
                      type="text"
                      value={k.subtext}
                      onChange={(e) => handleKpiChange(k.id, "subtext", e.target.value)}
                      className="w-full px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-xs text-slate-300"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 5: FEATURE FLAGS */}
        {activeTab === "flags" && (
          <div className="mt-6 bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-white">System Feature Flags</h2>
              <p className="text-xs text-slate-300 mt-1">
                Toggle live website modules on demand without redeploying code.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: "enableLiveChat" as const, title: "Radie Live Chat Assistant", desc: "Floating widget in bottom-right corner with adaptive lead capture." },
                { key: "enableBlog" as const, title: "Technical Blog Platform", desc: "Enable /#/blog directory and article reading pages." },
                { key: "enableNews" as const, title: "Newsfeed & Dispatches", desc: "Enable /#/news company announcements and press releases." },
                { key: "enableAnnouncementBar" as const, title: "Top Header Announcement Ticker", desc: "Sleek broadcast ticker bar at the top of the header." },
                { key: "enableEvents" as const, title: "Upcoming Technical Briefings", desc: "Display upcoming briefings and reservation cards." },
                { key: "enableTestimonials" as const, title: "Client Reviews Carousel", desc: "Display verified reviews and quotes on the website." },
                { key: "enableCareers" as const, title: "Careers Portal", desc: "Enable job application form and open positions at /#/careers." },
                { key: "enablePricingCalculator" as const, title: "Pricing Schedule", desc: "Show commercial pricing packages at /#/pricing." },
                { key: "maintenanceMode" as const, title: "Maintenance Mode Banner", desc: "Display active maintenance notification banner." },
              ].map((f) => (
                <div key={f.key} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-bold text-white">{f.title}</h4>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">{f.desc}</p>
                  </div>
                  <button
                    onClick={() => toggleFlag(f.key)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                      flags[f.key] ? "bg-primary" : "bg-slate-700"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        flags[f.key] ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 6: PAGES */}
        {activeTab === "pages" && (
          <div className="mt-6 bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-white">Page Publishing & Draft Management</h2>
              <p className="text-xs text-slate-300 mt-1">
                Toggle page availability between Published and Draft status.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-300">
                <thead className="bg-white/5 text-slate-200 text-[11px] font-bold uppercase tracking-wider border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">Page Name</th>
                    <th className="px-4 py-3">Route Path</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Last Updated</th>
                    <th className="px-4 py-3 text-right">Action Toggle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {pages.map((p) => (
                    <tr key={p.path} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3.5 font-bold text-white">{p.name}</td>
                      <td className="px-4 py-3.5 font-mono text-primary">/#${p.path.slice(1)}</td>
                      <td className="px-4 py-3.5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                            p.status === "published"
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                              : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                          }`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-slate-400">{p.lastUpdated}</td>
                      <td className="px-4 py-3.5 text-right">
                        <button
                          onClick={() => togglePageStatus(p.path)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                            p.status === "published"
                              ? "bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40"
                              : "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40"
                          }`}
                        >
                          {p.status === "published" ? "Switch to Draft" : "Publish Live"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 7: LEADS */}
        {activeTab === "leads" && (
          <div className="mt-6 bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-white">Lead Desk Inbox & Technical Inquiries</h2>
              <p className="text-xs text-slate-300 mt-1">
                Structured inquiries captured via Contact Form and Radie Live Chat Assistant.
              </p>
            </div>

            <div className="space-y-3">
              {leads.map((l) => (
                <div key={l.id} className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs font-bold text-primary bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-md">
                        {l.id}
                      </span>
                      <h4 className="text-sm font-bold text-white">{l.name}</h4>
                      <span className="text-xs text-slate-400">({l.service})</span>
                    </div>
                    <span className="text-xs font-mono text-slate-400">{l.timestamp}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                    <p>📧 Email: <a href={`mailto:${l.email}`} className="text-primary hover:underline">{l.email}</a></p>
                    <p>📞 Phone: <span className="font-mono">{l.phone || "Not Provided"}</span></p>
                  </div>
                  <div className="bg-black/20 p-3 rounded-lg border border-white/5 text-xs text-slate-200 whitespace-pre-wrap font-mono">
                    {l.message}
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    {l.status === "new" && (
                      <button
                        onClick={() => updateLeadStatus(l.id, "reviewed")}
                        className="bg-blue-500/20 text-blue-300 border border-blue-500/40 text-xs px-3 py-1 rounded-lg cursor-pointer"
                      >
                        Mark Reviewed
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 8: EVENTS */}
        {activeTab === "events" && (
          <div className="mt-6 bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-white">Upcoming Technical Briefings Manager</h2>
              <form onSubmit={handleAddEvent} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <input
                  type="text"
                  required
                  placeholder="Event Title"
                  value={newEventTitle}
                  onChange={(e) => setNewEventTitle(e.target.value)}
                  className="px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white"
                />
                <input
                  type="text"
                  placeholder="Category"
                  value={newEventCategory}
                  onChange={(e) => setNewEventCategory(e.target.value)}
                  className="px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white"
                />
                <button
                  type="submit"
                  className="sm:col-span-2 py-2 bg-primary text-slate-950 font-bold rounded-xl text-xs uppercase cursor-pointer"
                >
                  Add Briefing →
                </button>
              </form>
            </div>

            <div className="space-y-2">
              {events.map((e) => (
                <div key={e.id} className="bg-white/5 p-3 rounded-xl flex items-center justify-between">
                  <span className="text-xs font-bold text-white">{e.title}</span>
                  <button onClick={() => handleDeleteEvent(e.id)} className="text-rose-400 text-xs font-bold cursor-pointer">Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 9: GLOBAL CONTENT */}
        {activeTab === "content" && (
          <div className="mt-6 bg-[#101c2a] border border-white/15 rounded-2xl p-6 sm:p-8 space-y-6">
            <h2 className="text-lg font-bold text-white">Global Content & Hero Section Editor</h2>
            <form onSubmit={handleSaveContent} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Announcement Ticker Text</label>
                <input
                  type="text"
                  value={announcement}
                  onChange={(e) => setAnnouncementState(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Hero Tagline</label>
                <textarea
                  rows={2}
                  value={siteContent.heroTagline}
                  onChange={(e) => setSiteContentState({ ...siteContent, heroTagline: e.target.value })}
                  className="w-full px-3.5 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white"
                />
              </div>
              <button
                type="submit"
                className="bg-primary hover:bg-sky-400 text-slate-950 font-bold px-6 py-2.5 rounded-xl text-xs uppercase tracking-wider shadow-md cursor-pointer"
              >
                Save Global Content →
              </button>
            </form>
          </div>
        )}
      </div>
    </main>
  );
}
